import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

const soon = ['Overview', 'Network']

const adminLinks: [string, string][] = [
  ['/admin/users', 'Users'],
  ['/admin/roles', 'Roles'],
  ['/admin/permissions', 'Permissions'],
  ['/admin/tenants', 'Tenants'],
  ['/admin/api-tokens', 'API tokens'],
  ['/admin/credentials', 'Credentials'],
  ['/admin/locations', 'Location filters'],
]

const serif = { fontFamily: 'var(--v3-font-serif)' } as const

function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 mt-7 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted)]">
      {children}
    </p>
  )
}

export function LedgerShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex h-full bg-[var(--color-bg)] text-[var(--color-fg)]"
      style={{ fontFamily: 'var(--v3-font-sans)' }}
    >
      <aside className="flex w-[240px] shrink-0 flex-col border-r border-[var(--color-border)] px-6 py-8">
        <div className="border-b-2 border-[var(--v3-rule-strong)] pb-3">
          <span className="text-[26px] font-semibold leading-none tracking-[-0.01em]" style={serif}>
            Sync<span className="text-[var(--color-accent)]">.</span>
          </span>
          <p className="mt-1.5 text-[11px] uppercase tracking-[0.14em] text-[var(--color-muted)]">Operations ledger</p>
        </div>

        <GroupLabel>Directory</GroupLabel>
        <nav aria-label="Primary" className="flex flex-col">
          <Link
            to="/locations"
            className="-ml-3 border-l-2 border-transparent py-1.5 pl-[10px] text-[14.5px] text-[var(--color-fg)] transition-colors hover:text-[var(--color-accent)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
            activeProps={{
              className: '!border-[var(--color-accent)] !bg-[var(--color-surface-2)] font-semibold',
              'aria-current': 'page',
            }}
          >
            Locations
          </Link>
        </nav>

        <GroupLabel>Administration</GroupLabel>
        <nav aria-label="Administration" className="flex flex-col">
          {adminLinks.map(([to, label]) => (
            <Link
              key={to}
              to={to as '/locations'}
              className="-ml-3 border-l-2 border-transparent py-1.5 pl-[10px] text-[14.5px] text-[var(--color-fg)] transition-colors hover:text-[var(--color-accent)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
              activeProps={{
                className: '!border-[var(--color-accent)] !bg-[var(--color-surface-2)] font-semibold',
                'aria-current': 'page',
              }}
            >
              {label}
            </Link>
          ))}
        </nav>

        <GroupLabel>Coming</GroupLabel>
        <div className="flex flex-col">
          {soon.map((label) => (
            <span
              key={label}
              aria-disabled="true"
              className="cursor-default py-1.5 text-[14.5px] italic text-[var(--color-muted)] opacity-70"
              style={serif}
            >
              {label}
            </span>
          ))}
        </div>

        <p
          className="mt-auto border-t border-[var(--color-border)] pt-3 text-[11px] text-[var(--color-muted)]"
          style={serif}
        >
          Ledger edition
        </p>
      </aside>

      <main className="min-w-0 flex-1 overflow-auto px-12 py-10">
        <div className="mx-auto max-w-[880px]">{children}</div>
      </main>
    </div>
  )
}
